import { ServerError } from '@apollo/client';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import type { GraphQLError } from 'graphql';
import objectHasProperty from '@tools/objectHasProperty';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { fetchClientWithRetries } from '@tools/fetchClient';
import { ResponseNotOk } from '@cvent/fetch/middleware/index';
import { authExpired } from './authExpired';

const LOG = LoggerFactory.create('apollo/onErrorLink');

export interface UnauthenticatedError extends GraphQLError {
  errorInfo: {
    code: string;
  };
}

function isUnauthenticatedError(error: Error): error is UnauthenticatedError {
  if (error == null) {
    return false;
  }

  // Detects unauthorized responses from the AH graph
  if (
    objectHasProperty(error, 'errorInfo') &&
    typeof error.errorInfo === 'object' &&
    objectHasProperty(error.errorInfo, 'code')
  ) {
    return error.errorInfo.code === '401' || error.errorInfo.code === 'UNAUTHENTICATED';
  }

  // Detects unauthorized responses from the discussions graph
  if (
    objectHasProperty(error, 'extensions') &&
    typeof error.extensions === 'object' &&
    objectHasProperty(error.extensions, 'code')
  ) {
    return error.extensions.code === 'FORBIDDEN' && error?.message === 'Not authorized';
  }

  return false;
}

function isServerError(error: Error): error is ServerError {
  return objectHasProperty(error, 'statusCode');
}

export function handleError(error: ErrorResponse) {
  const { networkError, graphQLErrors } = error;

  if (typeof window === 'undefined') {
    return;
  }

  if (isServerError(networkError)) {
    // 403 is returned if CSRF token is expired. A reload will trigger a new CSRF token to be generated
    // 401 is returned if CSRF token is invalid. Redirect to logout page if provided
    if (networkError.statusCode === 403) {
      window.location.reload();
    } else if (networkError.statusCode === 401) {
      if (typeof networkError.result === 'object' && networkError.result?.redirectUrl) {
        window.location.href = networkError.result.redirectUrl;
      } else {
        window.location.reload();
      }
    }
  }

  // The authorization call only returns a `Not authorized` message, so we use it here to check if any of the
  // errors returned by the graphQL operation is related to authorization.
  const hasUnauthorizedError = graphQLErrors?.find(isUnauthenticatedError);
  if (hasUnauthorizedError) {
    // Check that we're actually no longer authenticated. Resiliency against rogue services throwing 401's.
    LOG.info('Checking if user is authenticated');
    fetchClientWithRetries.get('/hub/api/verify', { credentials: 'include' }).catch(verifyError => {
      if (verifyError instanceof ResponseNotOk && verifyError.response.status === 401) {
        LOG.info('User is not authenticated', verifyError);
        authExpired(true);
      } else {
        LOG.warn('There was an error verifying the auth token', verifyError);
      }
    });
  }
}

/**
 * Custom error link to handle 401 errors thrown by the graph. We've seen 401s at the component level that are
 * not handled and sometimes keep re-trying (i.e. subscriptions). This error link catches all graphQL errors
 * globally and handles 401s by redirecting the user to the login screen
 */
export const onErrorLink = onError(handleError);
